"use client";

import { useMemo, useState } from "react";
import {
  GetThoughtsRequest,
  MindstringPrototype,
  Thought,
  ThoughtDimension,
} from "@/generated/fastapi";
import { useThoughtsQuery } from "@/features/thoughts";
import type { BarChartFrequencyData } from "@/shared/ui/charts/GenericBarChart";
import GenericBarChart from "@/shared/ui/charts/GenericBarChart";
import { GenericBarChartSkeleton } from "@/shared/ui/charts/GenericBarChart";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericSegment from "@/shared/ui/controls/GenericSegment";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import { useAuth, useUserChoices } from "@/state/globalStore";
import {
  DIM_ATTR_CANONOICAL_LIST,
  getAssessmentDimensionLabel,
  getLikert5pAgreementOptions,
  mindstringPrototypes,
  normalizeVisibleMindstringPrototype,
} from "@/shared/config/backend";
import {
  ReflectionProgressBar,
  ReflectionProgressStats,
} from "./ReflectionProgress";
import { ThoughtDimensionAttributesExtended } from "@/shared/types";
import { Box, Heading, Spacer, VStack } from "@chakra-ui/react";

function useFrequencyData(
  thoughts: Thought[],
  options = getLikert5pAgreementOptions(),
  dimensions = DIM_ATTR_CANONOICAL_LIST,
) {
  return useMemo(() => {
    const optionMap = new Map(options.map((o) => [o.value, o.label]));
    const initMap = Object.fromEntries(options.map((o) => [o.label, 0]));

    const frequencyByDimension = Object.fromEntries(
      [...dimensions, "all"].map((dim) => [dim, { ...initMap }]),
    ) as Record<ThoughtDimensionAttributesExtended, Record<string, number>>;

    let countUnscored = 0,
      countPartial = 0,
      countFull = 0,
      countReflections = 0;

    thoughts.forEach((thought) => {
      let scored = 0,
        unscored = 0;
      dimensions.forEach((dim) => {
        const dimObj = thought[dim as keyof Thought] as ThoughtDimension | null;
        if (!dimObj?.score) return unscored++;
        const label = optionMap.get(dimObj.score.toString());
        if (label) {
          frequencyByDimension[dim][label]++;
          frequencyByDimension["all"][label]++;
          scored++;
          countReflections++;
        }
      });
      if (scored === 0) countUnscored++;
      else if (scored < dimensions.length) countPartial++;
      else countFull++;
    });

    const toArray = (map: Record<string, number>): BarChartFrequencyData[] =>
      Object.entries(map).map(([answer, count]) => ({ answer, count }));

    return {
      frequencyByDimension: Object.fromEntries(
        Object.entries(frequencyByDimension).map(([dim, map]) => [
          dim,
          toArray(map),
        ]),
      ) as Record<ThoughtDimensionAttributesExtended, BarChartFrequencyData[]>,
      countUnscoredThoughts: countUnscored,
      countPartiallyScoredThoughts: countPartial,
      countFullyScoredThoughts: countFull,
      countReflectionsProvided: countReflections,
    };
  }, [thoughts, dimensions, options]);
}

//
// ──────────────────────────────────────────────────────────────
// HOOK: fetch thoughts from backend
// ──────────────────────────────────────────────────────────────

function useFetchThoughts(
  situationId: string,
  prototype: MindstringPrototype,
  selected_ids: string[],
) {
  const request = useMemo<GetThoughtsRequest>(
    () => ({
      situation_id: situationId,
      prototype,
      selected_ids,
    }),
    [prototype, selected_ids, situationId],
  );

  const {
    data = [],
    isLoading,
    isError,
  } = useThoughtsQuery(request, {
    enabled: selected_ids.length > 0,
  });

  return { thoughts: data, isLoading, isError };
}

//
// ──────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────────
//

interface BiasDetectorV2Props {
  situationId: string;
  prototype: MindstringPrototype;
}

export default function BiasDetector({
  situationId,
  prototype,
}: BiasDetectorV2Props) {
  const { isUserAdmin } = useAuth();
  const normalizedPrototype = normalizeVisibleMindstringPrototype(
    prototype,
    isUserAdmin,
  );
  const { getSelectedThoughts } = useUserChoices();
  const { dimensionItems: fallbackDimensionItems } = useOptionItems();
  const dimensionItems = useMemo(
    () =>
      fallbackDimensionItems.map((item) =>
        item.value === "all"
          ? item
          : {
              ...item,
              label: getAssessmentDimensionLabel({
                dimension: item.value,
                prototype: normalizedPrototype,
              }),
            },
      ),
    [fallbackDimensionItems, normalizedPrototype],
  );

  // Local state for GenericSwitch
  const [selectedDimension, setSelectedDimension] =
    useState<ThoughtDimensionAttributesExtended>("dim0");

  // Step 1: Get selected thought IDs
  const selected_ids = useMemo(() => {
    return getSelectedThoughts(normalizedPrototype, situationId).flatMap(
      (st) => st.thoughts_ids,
    );
  }, [getSelectedThoughts, normalizedPrototype, situationId]);

  // Step 2: Fetch thoughts from backend
  const {
    thoughts: thoughtsToAnalyze,
    isLoading,
    isError,
  } = useFetchThoughts(situationId, normalizedPrototype, selected_ids);

  // Step 3: Compute frequency data
  const {
    frequencyByDimension,
    countUnscoredThoughts,
    countPartiallyScoredThoughts,
    countFullyScoredThoughts,
    countReflectionsProvided,
  } = useFrequencyData(thoughtsToAnalyze);

  if (selected_ids.length === 0) {
    return (
      <GenericBarChartSkeleton emptyMessage="Please select profile content first." />
    );
  }

  // Loading state
  if (isLoading) {
    return <GenericBarChartSkeleton emptyMessage="Loading thoughts..." />;
  }

  if (isError) {
    return (
      <GenericBarChartSkeleton emptyMessage="Could not load thoughts right now." />
    );
  }

  // No thoughts selected
  if (!thoughtsToAnalyze || thoughtsToAnalyze.length === 0) {
    return (
      <GenericBarChartSkeleton emptyMessage="No thoughts available in the selected profile content." />
    );
  }

  const hasReflections = countReflectionsProvided > 0;

  return (
    <Box>
      <VStack align="stretch">
        <GenericDrawerInfo
          description="Review reflection coverage and distribution for the selected confirmed thoughts."
          items={[
            {
              label: mindstringPrototypes[normalizedPrototype],
              colorPalette: "purple",
            },
            {
              label: `${thoughtsToAnalyze.length} thoughts`,
              colorPalette: "blue",
            },
            {
              label: `${countReflectionsProvided} reflections`,
              colorPalette: hasReflections ? "green" : "gray",
            },
          ]}
        />

        <ReflectionProgressBar
          totalReflections={
            thoughtsToAnalyze.length * DIM_ATTR_CANONOICAL_LIST.length
          }
          scoredReflections={countReflectionsProvided}
        />
        <ReflectionProgressStats
          countUnscoredThoughts={countUnscoredThoughts}
          countPartiallyScoredThoughts={countPartiallyScoredThoughts}
          countFullyScored={countFullyScoredThoughts}
        />
      </VStack>

      <Spacer h="6" />

      {hasReflections ? (
        <VStack align="stretch" gap="3">
          <Heading size="sm">Reflections distribution by dimension</Heading>
          <GenericSegment<ThoughtDimensionAttributesExtended>
            activeState={selectedDimension}
            setActiveState={(value) => {
              setSelectedDimension(value);
            }}
            options={dimensionItems}
          />

          <Box minW="0" w="full">
            <GenericBarChart
              frequencyData={frequencyByDimension[selectedDimension]}
              chartColor="blue.solid"
              showBars
              showArea
            />
          </Box>
        </VStack>
      ) : (
        <GenericBarChartSkeleton emptyMessage="No reflections yet provided." />
      )}
    </Box>
  );
}
